import React from "react";

import { faArrowRightLong } from "@fortawesome/sharp-regular-svg-icons/faArrowRightLong";
import clsx from "clsx";
import Balancer from "react-wrap-balancer";

import {
  type VariantBackgroundColorEnum,
  variantBackgroundColor,
} from "~styles/common/variant.backgroundColor.css";
import { type GetSprinklesArgs, getSprinkles } from "~styles/getSprinkles.css";

import { useMatchMedia } from "~hooks/use_match_media/use_match_media";

import { Box } from "~components/Box";
import { ButtonLink, type ButtonLinkProps } from "~components/ButtonLink";
import {
  FormHubspotDialog,
  type FormHubspotDialogProps,
} from "~components/FormHubspotDialog";
import {
  textWrapper,
  videoWrapper,
  wrapper,
} from "~components/HeroBackgroundVideo/styles.css";
import { StoryblokEditable } from "~components/StoryblokEditable";
import { StoryblokRichText } from "~components/StoryblokRichText";
import { Video, type VideoProps } from "~components/Video";

import type { StoryblokBlok } from "~types/storyblok.types";

export type HeroBackgroundVideoProps = StoryblokBlok & {
  video?: Array<StoryblokBlok & VideoProps>;
  videoTablet?: Array<StoryblokBlok & VideoProps>;
  titleTextAppearance?: Extract<
    GetSprinklesArgs["textAppearance"],
    "h1" | "h2" | "h3" | "h4" | "h5" | "h6"
  >;
  title?: string;
  description?: string;
  cta: Array<StoryblokBlok & (ButtonLinkProps | FormHubspotDialogProps)>;
  theme?: VariantBackgroundColorEnum;
};

const BUTTON_PROPS = {
  iconRight: faArrowRightLong,
};

export function HeroBackgroundVideo({
  video: videoBlokArray,
  videoTablet: videoTabletBlokArray,
  title,
  titleTextAppearance,
  description,
  cta: ctaLinkBlokArray,
  theme = "background_charcoal",
  ...rest
}: HeroBackgroundVideoProps) {
  const [videoBlok] = videoBlokArray || [];
  const [videoTabletBlok] = videoTabletBlokArray || [];
  const [cta] = ctaLinkBlokArray || [];
  const buttonLinkIsFormHubspotDialog = cta?.component === "FormHubspotDialog";
  const isButtonLink = cta?.component === "ButtonLink";

  const isViewportTablet = useMatchMedia({ max: "TABLET" });
  const videoBlokToUse = isViewportTablet ? videoTabletBlok : videoBlok;

  return (
    <StoryblokEditable {...rest}>
      <Box className={clsx(wrapper, variantBackgroundColor[theme])}>
        {videoBlok && (
          <Video
            {...videoBlokToUse}
            videoClassName={getSprinkles({
              height: "100%",
              width: "100%",
            })}
            className={videoWrapper}
            hasA11yPlayButton
          />
        )}
        <Box zIndex="1" position="relative" className={textWrapper}>
          <Box>
            {title && (
              <Box
                fontFamily="formulaCondensed"
                maxWidth="gridSpan8"
                marginBottom={description ? "spacing2" : "spacing0"}
              >
                <Balancer
                  {...(titleTextAppearance && { as: titleTextAppearance })}
                  className={getSprinkles({
                    textAppearance: titleTextAppearance,
                  })}
                >
                  {title}
                </Balancer>
              </Box>
            )}
            {description && (
              <StoryblokRichText
                isTextBalanced
                width="auto"
                maxWidth="gridSpan8"
                text={description}
              />
            )}
            {cta && (
              <Box
                display="inline-flex"
                width="max-content"
                marginBottom="spacing1"
                marginTop="spacing3"
              >
                {isButtonLink && (
                  <ButtonLink marginTop="auto" {...(cta as ButtonLinkProps)} />
                )}
                {buttonLinkIsFormHubspotDialog && (
                  <FormHubspotDialog
                    {...(cta as FormHubspotDialogProps)}
                    marginTop="auto"
                    buttonProps={BUTTON_PROPS}
                    // Set default conversionType to "contact_sales" if not provided
                    conversionType={
                      (cta as FormHubspotDialogProps).conversionType ||
                      "contact_sales"
                    }
                  />
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </StoryblokEditable>
  );
}
